import { useEffect, useState } from "react";
import { BrandLogo } from "@/components/BrandLogo";
import { Progress } from "@/components/ui/progress";

// Phase A.2 (Mistral migration, 2026-06): paste-only flow. The "Recherche
// de la politique de confidentialité" stage was retired with the URL
// discovery layer in PR #284; the active steps are now (1) observation of
// the homepage by the behavioural scanner, (2) LLM extraction on the
// pasted policy text, (3) deterministic validators + report assembly.
// 2026-07-30: two stage lists — a URL-only surface scan has no pasted
// policy, so showing "Analyse de la politique collée" there was wrong
// (spotted in the prod walkthrough). Vendor name dropped from user copy.
const STAGES_WITH_PASTE = [
  "Observation du comportement de votre site...",
  "Analyse de la politique collée...",
  "Vérification des faits extraits...",
  "Génération du rapport...",
];

const STAGES_SURFACE_ONLY = [
  "Observation du comportement de votre site...",
  "Analyse des cookies et traceurs observés...",
  "Génération du rapport...",
];

const TOTAL_DURATION_MS = 60_000;
const STAGE_INTERVAL_MS = 9_000;

const AuditLoading = ({ url, hasPaste = true }: { url: string; hasPaste?: boolean }) => {
  const STAGES = hasPaste ? STAGES_WITH_PASTE : STAGES_SURFACE_ONLY;
  const [stageIndex, setStageIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const stageTimer = setInterval(() => {
      setStageIndex((i) => Math.min(i + 1, STAGES.length - 1));
    }, STAGE_INTERVAL_MS);

    const start = Date.now();
    const progressTimer = setInterval(() => {
      const elapsed = Date.now() - start;
      // Cap at 95% — final 5% only when response arrives
      const pct = Math.min(95, (elapsed / TOTAL_DURATION_MS) * 95);
      setProgress(pct);
    }, 200);

    return () => {
      clearInterval(stageTimer);
      clearInterval(progressTimer);
    };
  }, []);

  let hostname = url;
  try { hostname = new URL(url).hostname; } catch { /* noop */ }

  return (
    <div className="mx-auto max-w-2xl text-center space-y-8 py-8">
      <div className="relative mx-auto w-24 h-24 flex items-center justify-center">
        <div className="absolute inset-0 rounded-full bg-primary/10 animate-ping" />
        <div className="relative w-20 h-20 rounded-full bg-primary/15 flex items-center justify-center animate-pulse">
          <BrandLogo className="h-10 w-10 text-primary" />
        </div>
      </div>

      <div className="space-y-3">
        <h2
          className="text-2xl md:text-3xl font-bold tracking-tight"
          style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
        >
          Analyse de <span className="text-primary">{hostname}</span>
        </h2>
        <p className="text-base md:text-lg text-foreground/80 transition-opacity duration-500" key={stageIndex}>
          {STAGES[stageIndex]}
        </p>
      </div>

      <div className="space-y-2 max-w-md mx-auto">
        <Progress value={progress} className="h-2" />
        <p className="text-xs text-muted-foreground">
          Analyse en cours : environ 20 à 40 secondes
        </p>
      </div>

      <div className="flex justify-center gap-2">
        {STAGES.map((_, i) => (
          <div
            key={i}
            className={`h-1.5 w-8 rounded-full transition-colors ${
              i <= stageIndex ? "bg-primary" : "bg-muted"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default AuditLoading;
